import { Observable, Subject } from 'rxjs';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';

interface ISocketInfo<T> {
  id: any;
  socket: WebSocketSubject<T>;
  lastTrafficTime: number;
  subject: Subject<any>;
}

export class SocketManager<T> {
  private _sockets: ISocketInfo<T>[] = [];
  private static readonly KEEP_ALIVE_INTERVAL_MS = 1000 * 25;
  private static readonly MAX_TTL_MS = 1000 * 60 * 2;

  constructor() {
    this.keepAlive();
  }

  open(id: any, url: string): Observable<any> {
    this.check(id);

    const dataSubject = new Subject<any>();
    const data$ = dataSubject.asObservable();

    const webSocketSubjectConfig = {
      url: url,
      closeObserver: {
        next(closeEvent: { code: number; type: any; reason: any }) {
          if (closeEvent.code !== 1006) {
            // happens in the "normal" case - don't log out but do send message in case it needs handling
            console.log('Socket closed.', closeEvent.type, closeEvent.code, closeEvent.reason);
          }
          dataSubject.error({ code: closeEvent.code, reason: closeEvent.reason });
        },
      },
    };

    const ws: WebSocketSubject<T> = webSocket(webSocketSubjectConfig);
    this._sockets.push({ id: id, socket: ws, lastTrafficTime: Date.now(), subject: dataSubject });
    ws.subscribe(
      (data: any) => {
        console.debug('Socket data received ID:', id, 'Data:', data);
        this.updateSocket(id);
        dataSubject.next({ socketId: id, ...data });
      },
      (error: any) => {
        dataSubject.error({ socketId: id, ...error });
        this.close(id);
      },
      () => {
        // do nothing? should be handled by closeObserver
        console.log('WebSocket complete. Closed by server?');
      },
    );

    return data$;
  }

  close(id: any): boolean {
    const index = this._sockets.findIndex((socketInfo) => socketInfo.id === id);
    if (index > -1) {
      this.closeSocket(this._sockets[index]);
      this._sockets.splice(index, 1);
      return true;
    }

    return false;
  }

  closeAll(): void {
    this._sockets.forEach((socketInfo) => {
      this.closeSocket(socketInfo);
    });
    this._sockets = [];
  }

  private check(id: any): void {
    const existing = this._sockets.find((si) => si.id === id);
    if (existing) {
      console.warn(`Id ${id} already has a socket open`);
    }
  }

  private keepAlive(): void {
    setInterval(() => {
      this.removeDeadSockets();
      this._sockets.forEach((si) => {
        SocketManager.pingServer(si.socket);
      });
    }, SocketManager.KEEP_ALIVE_INTERVAL_MS);
  }

  private updateSocket(id: any): void {
    const socketInfo = this._sockets.find((si) => si.id === id);
    if (socketInfo) {
      const now = Date.now();
      console.debug(`Updating lastTrafficTime from ${socketInfo.lastTrafficTime} to ${now}`);
      socketInfo.lastTrafficTime = now;
    }
  }

  private closeSocket(socketInfo: ISocketInfo<any>): void {
    console.log('Closing socket', socketInfo.id);
    if (!socketInfo.subject.closed) {
      socketInfo.subject.complete();
    }

    socketInfo.socket.unsubscribe();
  }

  private removeDeadSockets(): void {
    const copy = [...this._sockets];
    const now = Date.now();
    copy.forEach((si) => {
      if (now - si.lastTrafficTime > SocketManager.MAX_TTL_MS) {
        console.log(`Dead socket: ${si.id}`);
        this.close(si.id);
      }
    });
  }

  private static pingServer(ws: WebSocketSubject<any>): void {
    console.debug('Socket keepalive ping', Date.now(), ws);
    ws.next({ type: 'ping' });
  }
}
